import * as _ from 'lodash';

import {
  CreateRoleParamsType,
  CreateTrialUser,
  CreateSheetPayloadType,
  DeleteTrialUser,
  FetchUsers,
  GetUsersOperatoriesPayloadType,
  GetUsersOperatoriesResType,
  SubscriptionStatusCountsResponse,
  updateUserDetailsPayload
} from './types';
import axiosInstance from '../../../axiosInstance';
import { setShowAlert } from '../../../state/utilts/utilsSlice';
import { TOAST_TYPE } from '../../../utils/constantTypes';
import {
  AccessToken,
  ImportCDTCodesPayloadType,
  importPlansFromSheetPayloadType,
  ImportInternalPlansPayloadType,
  UpdatePracticeDetailsPayloadType,
  UpdateUserDataParamsType,
  GetInvoicesByCustomerIdPayloadType,
  EnablePracticeLocationPayloadType,
  ToggleIsSheetLockPayloadType
} from '../../../state/user/interfaces';
import { resetAuth } from '../../../state/auth/authSlice';
import { TOAST_MSGS } from './../../../constants/index';
import { isTrialUser } from '../../../utils';
import { importCodeToPlanAsync } from '../../../state/subscriptions/subscriptionSlice';

export const createTrialUserService = async (
  payload: CreateTrialUser,
  { dispatch, rejectWithValue }
) => {
  try {
    const data = _.get(payload, 'data', {});
    const accessToken = _.get(payload, 'accessToken', null);
    const response = await axiosInstance.post(
      'auth/trial-user',
      { ...data },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }
    );
    dispatch(
      setShowAlert({
        show: true,
        message: response?.data?.msg,
        type: TOAST_TYPE.SUCCESS
      })
    );
    return response;
  } catch (err) {
    if (err?.response?.data.statusCode !== 401) {
      dispatch(
        setShowAlert({
          show: true,
          message: err?.response?.data?.msg || err?.response?.data?.message,
          type: TOAST_TYPE.ERROR
        })
      );
    }
    return rejectWithValue(err?.response?.data);
  }
};

export const fetchUsersService = async (
  payload: FetchUsers,
  { dispatch, rejectWithValue }
) => {
  try {
    const filters = _.get(payload, 'filters', {});
    const accessToken = _.get(payload, 'accessToken', null);
    const response = await axiosInstance.get('user/all', {
      params: filters,
      headers: { Authorization: `Bearer ${accessToken}` }
    });
    return response.data;
  } catch (err) {
    dispatch(
      setShowAlert({
        show: true,
        message: err?.response?.data?.msg || err?.response?.data?.message,
        type: TOAST_TYPE.ERROR
      })
    );
    return rejectWithValue(err?.response?.data);
  }
};

export const fetchSubscriptionStatusCountsService = async (
  payload: { accessToken: string },
  { dispatch }
): Promise<SubscriptionStatusCountsResponse> => {
  try {
    const accessToken = _.get(payload, 'accessToken', null);
    const response = await axiosInstance.get(
      'user/admin/subscription-status-counts',
      {
        headers: { Authorization: `Bearer ${accessToken}` }
      }
    );
    return response.data;
  } catch (err) {
    dispatch(
      setShowAlert({
        show: true,
        message: err?.response?.data?.msg || err?.response?.data?.message,
        type: TOAST_TYPE.ERROR
      })
    );
    throw err;
  }
};

export const createRoleService = async (
  payload: CreateRoleParamsType,
  { dispatch }
) => {
  try {
    const roleData = _.omit(payload, ['permissions', 'accessToken']);
    const roleRes = await axiosInstance.post('role', roleData, {
      headers: {
        Authorization: `Bearer ${payload.accessToken}`
      }
    });
    const roleId = _.get(roleRes, 'data.data.id');
    if (payload?.permissions?.length) {
      await axiosInstance.post(
        'role/assign-permission-to-role',
        {
          roleId,
          permissionIds: _.get(payload, 'permissions')
        },
        {
          headers: {
            Authorization: `Bearer ${payload.accessToken}`
          }
        }
      );
    }
    return { ...roleRes.data, data: { ...roleData, id: roleId } };
  } catch (err) {
    if (err?.response?.data?.statusCode !== 401) {
      dispatch(
        setShowAlert({
          show: true,
          message:
            err?.response?.data?.msg ||
            'An error occurred while creating role.',
          type: TOAST_TYPE.ERROR
        })
      );
    }
    throw new Error(
      err?.response?.data?.msg || 'An error occurred while creating role.'
    );
  }
};

export const deleteTrialUser = async (
  payload: DeleteTrialUser,
  { dispatch }
) => {
  try {
    const userId = _.get(payload, 'userId', null);
    const res = await axiosInstance.delete('user/trial-user', {
      params: {
        userId
      },
      headers: {
        Authorization: `Bearer ${payload.accessToken}`
      }
    });
    return res.data;
  } catch (err) {
    if (err?.response?.data?.statusCode !== 401) {
      dispatch(
        setShowAlert({
          show: true,
          message:
            err?.response?.data?.msg ||
            'An error occurred while deleting trial user.',
          type: TOAST_TYPE.ERROR
        })
      );
    }
    throw new Error(
      err?.response?.data?.msg || 'An error occurred while deleting trial user.'
    );
  }
};
export const updateUserDetails = async (payload: updateUserDetailsPayload) => {
  try {
    const data = _.get(payload, 'data');
    const res = await axiosInstance.post(
      'user/update',
      { ...data },
      {
        headers: {
          Authorization: `Bearer ${payload.accessToken}`
        }
      }
    );
    return res.data;
  } catch (err) {
    throw new Error(
      err?.response?.data?.msg || 'An error occurred while updating user.'
    );
  }
};

export const updateUser = async (
  payload: UpdateUserDataParamsType,
  { dispatch }
) => {
  try {
    const data = _.get(payload, 'data');
    const res = await axiosInstance.post(
      'user/update',
      { ...data },
      {
        headers: {
          Authorization: `Bearer ${payload.accessToken}`
        }
      }
    );
    return res.data;
  } catch (err) {
    // // To stop toast msg while updating email & phone number
    const sendEmailOtp = _.get(payload, 'data.sendEmailOtp');
    const sendMobileOtp = _.get(payload, 'data.sendMobileOtp');
    if (
      err?.response?.data?.statusCode !== 401 &&
      !sendEmailOtp &&
      !sendMobileOtp
    ) {
      dispatch(
        setShowAlert({
          show: true,
          message:
            err?.response?.data?.msg ||
            'An error occurred while updating user.',
          type: TOAST_TYPE.ERROR
        })
      );
    }
    throw new Error(
      err?.response?.data?.msg || 'An error occurred while updating user.'
    );
  }
};

export const uploadAgreementSignature = async (
  payload: {
    accessToken: string;
    signature: string;
    customerEmail: string;
    customerPracticeName: string;
    customerFullName: string;
    signedDate: string;
  }
) => {
  try {
    const response = await axiosInstance.post(
      'signature/upload',
      {
        signature: payload.signature,
        customerEmail: payload.customerEmail,
        customerPracticeName: payload.customerPracticeName,
        customerFullName: payload.customerFullName,
        signedDate: payload.signedDate
      },
      {
        headers: {
          Authorization: `Bearer ${payload.accessToken}`
        }
      }
    );

    return response.data;
  } catch (err) {
    throw new Error(
      err?.response?.data?.msg || 'An error occurred while uploading signature.'
    );
  }
};

export const getInvoicesByCustomerId = async (
  payload: GetInvoicesByCustomerIdPayloadType,
  { dispatch }
) => {
  try {
    const {
      accessToken,
      offset,
      limit,
      data: { customerId, subscriptionId, startDate, endDate, status }
    } = payload;

    const res = await axiosInstance.get(`user/invoices/${customerId}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`
      },
      params: {
        ...(offset !== undefined && { offset }),
        ...(limit !== undefined && { limit }),
        ...(subscriptionId && { subscriptionId }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(status && { status })
      }
    });

    return res.data;
  } catch (err) {
    if (err?.response?.data?.statusCode !== 401) {
      dispatch(
        setShowAlert({
          show: true,
          message:
            err?.response?.data?.msg ||
            'An error occurred while fetching invoices.',
          type: TOAST_TYPE.ERROR
        })
      );
    }
    throw new Error(
      err?.response?.data?.msg || 'An error occurred while fetching invoices.'
    );
  }
};

export const getNextInvoiceDateByCustomerId = async (payload, { dispatch }) => {
  try {
    const res = await axiosInstance.get(
      `user/getNextInvoiceDate/${payload?.data?.customerId}`,
      {
        headers: {
          Authorization: `Bearer ${payload.accessToken}`
        }
      }
    );
    return res.data;
  } catch (err) {
    if (err?.response?.data?.statusCode !== 401) {
      dispatch(
        setShowAlert({
          show: true,
          message:
            err?.response?.data?.msg ||
            'An error occurred while fetching next invoice date.',
          type: TOAST_TYPE.ERROR
        })
      );
    }
    throw new Error(
      err?.response?.data?.msg ||
        'An error occurred while fetching next invoice date.'
    );
  }
};

export const deleteUsersActiveSession = async (
  payload: AccessToken,
  { dispatch }
) => {
  try {
    await axiosInstance.delete('user/active-sessions', {
      headers: {
        Authorization: `Bearer ${payload?.accessToken}`
      }
    });
    localStorage.clear();
    dispatch(resetAuth());
  } catch (err) {
    throw new Error(
      err?.response?.data?.msg ||
        'An error occurred while deleting users active sessions.'
    );
  }
};

export const updatePractice = async (
  payload: Omit<UpdatePracticeDetailsPayloadType, 'data'> & {
    data: Partial<UpdatePracticeDetailsPayloadType['data']>;
  },
  { dispatch }
) => {
  try {
    return await axiosInstance.patch(
      `practice/${payload.practiceId}`,
      {
        ...payload.data
      },
      {
        headers: {
          Authorization: `Bearer ${payload?.accessToken}`
        }
      }
    );
  } catch (err) {
    throw new Error(
      err?.response?.data?.msg ||
        'An error occurred while updating practice details.'
    );
  }
};

export const getUserDetailsById = async (
  payload: Omit<UpdatePracticeDetailsPayloadType, 'data'> & {
    data: Partial<UpdatePracticeDetailsPayloadType['data']>;
  },
  { dispatch }
) => {
  try {
    return await axiosInstance.patch(
      `practice/${payload.practiceId}`,
      {
        ...payload.data
      },
      {
        headers: {
          Authorization: `Bearer ${payload?.accessToken}`
        }
      }
    );
  } catch (err) {
    throw new Error(
      err?.response?.data?.msg ||
        'An error occurred while fetching user details.'
    );
  }
};

export const importCDTCodes = async (
  payload: ImportCDTCodesPayloadType,
  { dispatch }
) => {
  try {
    const res = await axiosInstance.post(
      'importCDTCodes',
      {
        ...payload.data
      },
      {
        headers: {
          Authorization: `Bearer ${payload?.accessToken}`
        }
      }
    );
    const statusCode = _.get(res, 'data.statusCode', null);
    const msg = _.get(res, 'data.msg', null);
    if (statusCode === 200) {
      dispatch(
        setShowAlert({
          show: true,
          message: msg,
          type: TOAST_TYPE.SUCCESS
        })
      );
    }
  } catch (err) {
    throw new Error(
      err?.response?.data?.msg ||
        'An error occurred while fetching user details.'
    );
  }
};

export const importPlans = async (
  payload: importPlansFromSheetPayloadType,
  { dispatch }
) => {
  try {
    const res = await axiosInstance.post(
      'addPlansByUser',
      { ...payload?.data },
      {
        headers: {
          Authorization: `Bearer ${payload?.accessToken}`
        }
      }
    );
    return res?.data || {};
  } catch (err) {
    const errMsg = 'An error occurred while importing plans.';
    if (err?.response?.data.statusCode !== 401) {
      dispatch(
        setShowAlert({
          show: true,
          message: errMsg,
          type: TOAST_TYPE.ERROR
        })
      );
    }
    throw new Error(err?.response?.data?.msg || errMsg);
  }
};

export const fetchUserById = async (payload: any, { dispatch }) => {
  try {
    const res = await axiosInstance.post(
      'user/details',
      { ...payload?.data },
      {
        headers: {
          Authorization: `Bearer ${payload?.accessToken}`
        }
      }
    );
    return res?.data || {};
  } catch (err) {
    const errMsg = 'An error occurred while fetching user details.';
    if (err?.response?.data.statusCode !== 401) {
      dispatch(
        setShowAlert({
          show: true,
          message: errMsg,
          type: TOAST_TYPE.ERROR
        })
      );
    }
    throw new Error(err?.response?.data?.msg || errMsg);
  }
};

export const createAssistantUser = async ({
  accessToken,
  data
}: {
  accessToken: string | null;
  data: any;
}) => {
  try {
    const res = await axiosInstance.post(
      'user/add-assistant',
      { ...data },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }
    );
    return res?.data || {};
  } catch (err) {
    const errMsg = 'An error occurred while creating assistant.';
    throw new Error(err?.response?.data?.msg || errMsg);
  }
};

export const createSheetService = async (
  payload: CreateSheetPayloadType
): Promise<any> => {
  try {
    const res = await axiosInstance.post('users/create-sheet', payload?.data, {
      headers: {
        Authorization: `Bearer ${payload?.accessToken}`
      }
    });
    return res?.data || {};
  } catch (err) {
    const errMsg = 'An error occurred while creating sheet.';
    throw new Error(
      err?.response?.data?.msg || err?.response?.data?.message || errMsg
    );
  }
};

export const importInternalPlans = async (
  payload: ImportInternalPlansPayloadType
) => {
  try {
    const res = await axiosInstance.post(
      'procedure-codes-import',
      { ...payload?.data },
      { headers: { Authorization: `Bearer ${payload?.accessToken}` } }
    );
    return res?.data || {};
  } catch (err) {
    console.log('Import codes and plans failed:', err);
    return err?.response?.data;
  }
};

export const checkPracticeLocation = async ({
  accessToken,
  data: { officeId, userId, practiceIds, isLocationEnable }
}) => {
  try {
    const practiceLocationRes = await axiosInstance.get(
      `practice/location/status/${officeId}`,
      {
        params: {
          userId,
          practiceIds,
          isLocationEnable
        },
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }
    );
    const practiceLocationData = _.get(practiceLocationRes, 'data', {});
    const statusCode = _.get(practiceLocationData, 'statusCode');
    const msg = _.get(practiceLocationData, 'msg', '');

    return {
      statusCode,
      msg
    };
  } catch (err) {
    throw new Error(err?.response?.data?.msg);
  }
};

export const enablePracticeLocation = async (
  payload: EnablePracticeLocationPayloadType,
  { dispatch }
) => {
  try {
    const {
      accessToken,
      data: {
        officeId,
        userId,
        isProcedureCodeImported,
        role,
        practiceIds,
        isLocationEnable
      }
    } = payload;

    const { statusCode, msg } = await checkPracticeLocation({
      accessToken,
      data: { officeId, userId, practiceIds, isLocationEnable }
    });
    if (!isTrialUser(role) && !isProcedureCodeImported && statusCode === 200) {
      // show location enabled toast
      await dispatch(
        setShowAlert({
          show: true,
          message: msg,
          type: TOAST_TYPE.SUCCESS
        })
      );
      // // import procedure codes only once when location is turned on 1st time
      let retryCount = 2;
      let hasSuccess = false;
      while (!hasSuccess && retryCount > 0) {
        const importCodeRes = await importCodeToPlanAsync({
          accessToken: accessToken,
          payload: { userId: userId, officeId }
        });
        const statusCode = _.get(importCodeRes, 'data.statusCode');
        if (statusCode === 200) {
          localStorage.setItem('importCodes', 'true');
          // // import CDT Codes only once when location is turned on 1st time
          importCDTCodes(
            {
              accessToken: accessToken,
              data: {
                userId,
                officeId
              }
            },
            { dispatch }
          );
          hasSuccess = true;
        }
        retryCount--;
      }
    }
  } catch (err) {
    throw new Error(err?.response?.data?.msg);
  }
};

export const fetchOperatoriesList = async (
  payload: GetUsersOperatoriesPayloadType
): Promise<GetUsersOperatoriesResType[]> => {
  try {
    const operatoriesListRes = await axiosInstance.post(
      `sikka/users-operatories`,
      { data: [...payload?.data] },
      {
        headers: {
          Authorization: `Bearer ${payload?.accessToken}`
        }
      }
    );
    const operatoriesListData = _.get(operatoriesListRes, 'data', {});
    return operatoriesListData?.data || {};
  } catch (err) {
    throw new Error(err?.response?.data?.msg);
  }
};

export const toggleIsSheetLock = async (
  payload: ToggleIsSheetLockPayloadType,
  { dispatch }
): Promise<void> => {
  try {
    await axiosInstance.post(
      'unlockSheet',
      { ...payload?.data },
      {
        headers: {
          Authorization: `Bearer ${payload?.accessToken}`
        }
      }
    );
  } catch (err) {
    dispatch(
      setShowAlert({
        show: true,
        message: TOAST_MSGS.TOGGLE_SHEET_IS_LOCK,
        type: TOAST_TYPE.ERROR
      })
    );
  }
};

export const updateUserByAdmin = async (data: any, accessToken: any) => {
  try {
    await axiosInstance.post(
      'user/admin/update',
      {
        data
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }
    );
  } catch (err) {
    throw new Error(err?.response?.data?.msg);
  }
};
